<template>
	<el-dialog
		class="quick-create-group-dialog im-dialog-shell im-dialog-shell--lg"
		title="创建群聊"
		:visible.sync="show"
		:width="dialogWidth"
		:before-close="close"
		custom-class="quick-create-group-panel"
		top="4vh"
		append-to-body
	>
		<div class="quick-create-group" :class="{ 'is-mobile': isMobileView }">
			<div class="left-box">
				<div class="panel-head">
					<el-input
						class="group-name-input"
						placeholder="群聊名称（选填）"
						v-model="groupName"
						size="small"
						maxlength="20"
						clearable
					></el-input>
					<el-input :placeholder="searchPlaceholder" v-model="searchText" size="small" clearable>
						<i class="el-icon-search el-input__icon" slot="suffix"> </i>
					</el-input>
					<tabs :items="sourceTabItems" :current="sourceTabIdx" @change="onSourceTabChange"></tabs>
					<div class="select-tools">
						<el-checkbox
							v-model="allVisibleCheckedProxy"
							:indeterminate="isSelectAllIndeterminate"
							:disabled="isSelectAllDisabled || selectAllLoading"
						>
							全选
						</el-checkbox>
						<i v-if="selectAllLoading" class="el-icon-loading select-loading"></i>
						<div class="select-tools__actions">
							<div class="result-badge">{{ currentListCount }}</div>
							<el-button
								type="text"
								icon="el-icon-delete"
								class="icon-btn clear-btn"
								:disabled="checkCount === 0"
								title="清空"
								@click="onClearChecked"
							></el-button>
						</div>
					</div>
				</div>
				<friend-group-quick-select
					v-if="currentSourceTab === 'friendGroup'"
					class="scroll-box"
					:groups="showFriendGroups"
					:checked-group-ids="checkedFriendGroupIds"
					:empty-title="currentEmptyTitle"
					@toggle-group="onToggleFriendGroup"
					@reach-bottom="onFriendGroupReachBottom"
				></friend-group-quick-select>
				<empty-state
					v-else-if="friendsHydrating"
					class="list-empty-state"
					icon="el-icon-loading"
					title="正在加载好友"
					compact
				></empty-state>
				<empty-state
					v-else-if="showFriends.length === 0"
					class="list-empty-state"
					icon="el-icon-search"
					:title="currentEmptyTitle"
					compact
				></empty-state>
					<virtual-scroller
						v-else
						ref="friendScroller"
						class="scroll-box"
						:items="showFriends"
						:item-height="friendRowHeight"
						:item-key-getter="getSourceFriendKey"
						@reach-bottom="onFriendListReachBottom"
					>
					<template v-slot="{ item }">
							<friend-item @click.native="onSwitchCheck(item)" :menu="false" :friend="item" size="small">
								<el-checkbox
									:value="isFriendChecked(item)"
									@change="setFriendChecked(item, $event)"
									@click.native.stop=""
									class="checkbox"
								></el-checkbox>
						</friend-item>
					</template>
				</virtual-scroller>
			</div>
			<div class="right-box">
				<div class="panel-head selection-head">
					<div class="panel-title compact-title">
						<i class="el-icon-user-solid"></i>
						<span>已选成员</span>
						<span class="inline-count">{{ checkCount }}</span>
					</div>
					<el-button
						v-if="checkCount > 0"
						type="text"
						icon="el-icon-delete"
						class="icon-btn clear-btn"
						title="清空"
						@click="onClearChecked"
					></el-button>
				</div>
				<empty-state
					v-if="checkedSelectionItems.length === 0"
					class="scroll-box list-empty-state compact-empty"
					:icon="checkedEmptyIcon"
					:title="checkedEmptyTitle"
					compact
				></empty-state>
						<virtual-scroller
							v-else
							class="scroll-box checked-scroll-box"
							:items="checkedSelectionItemsView"
							:item-height="54"
							:item-key-getter="getCheckedSelectionRowKey"
							@reach-bottom="onCheckedListReachBottom"
						>
						<template v-slot="{ item }">
							<div class="checked-item" @click="onRemoveCheckedItem(item)">
								<head-image :url="item.headImage" :name="item.showNickName || item.nickName || item.showName" :size="34"></head-image>
								<div class="checked-main">
									<div class="checked-name">{{ item.showNickName || item.nickName || item.showName }}</div>
								</div>
								<div class="close" title="移除">
									<i class="el-icon-close"></i>
								</div>
						</div>
					</template>
				</virtual-scroller>
			</div>
		</div>
		<span slot="footer" class="dialog-footer add-member-footer">
			<el-button @click="close" :disabled="submitting">取 消</el-button>
			<el-button type="primary" @click="onOk" :loading="submitting" :disabled="checkCount === 0 || submitting">
				创建
			</el-button>
		</span>
	</el-dialog>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import Tabs from '../common/Tabs.vue';
import VirtualScroller from '../common/VirtualScroller.vue';
import FriendItem from '../friend/FriendItem.vue';
import FriendGroupQuickSelect from '../friend/FriendGroupQuickSelect.vue';
import {
	attachContactSearchText,
	CONTACT_SEARCH_TEXT_KEY,
	matchesPreparedContactKeyword
} from '../../utils/contactSearchMatcher';
import {
	buildFriendGroupOption,
	countFriendGroupNewMembers,
	filterFriendGroupOptions,
	resolveFriendGroupMembers
} from '../../utils/friendGroupSelection';
import { resolveGroupChatDisplayName } from '../../utils/identityDisplay.js';
import { preloadChatRouteView } from '../../router/lazyViews.js';

const CHECKED_RESULT_PAGE_SIZE = 30;

function resolveIdKey(id) {
	return id === undefined || id === null || id === '' ? '' : String(id);
}

export default {
	name: "quickCreateGroup",
	components: {
		HeadImage,
		Tabs,
		VirtualScroller,
		FriendItem,
		FriendGroupQuickSelect
	},
	props: {
		dialogVisible: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			show: false,
			groupName: "",
			searchText: "",
			sourceTabIdx: 0,
			friends: [],
			friendLookupMap: Object.create(null),
				friendsHydrating: false,
				friendHydrationScheduled: false,
				checkedFriendIdMap: {},
				excludedFriendIdMap: {},
				groupExcludedFriendIdMap: {},
				allFriendsSelected: false,
				checkedFriendGroupIdMap: {},
				checkedViewLimit: CHECKED_RESULT_PAGE_SIZE,
				selectAllLoading: false,
				submitting: false
			};
		},
	computed: {
		isMobileView() {
			return this.configStore.isMobile;
		},
		dialogWidth() {
			return this.configStore.isMobile ? '100%' : '680px';
		},
		friendRowHeight() {
			return 42;
		},
		mine() {
			return this.userStore.userInfo;
		},
		maxMemberCount() {
			return this.$permission.getMaxMemberCount();
		},
		sourceTabItems() {
			return ['好友', '好友分组'];
		},
		currentSourceTab() {
			return this.sourceTabIdx === 1 ? 'friendGroup' : 'friend';
		},
		normalizedSearchText() {
			return String(this.searchText || '').trim().toLowerCase();
		},
		friendSelectionState() {
			const checkedFriendIds = [];
			const checkedFriendIdSet = new Set();
			Object.keys(this.checkedFriendIdMap).forEach((friendId) => {
				const friendKey = resolveIdKey(friendId);
				if (!friendKey) {
					return;
				}
				checkedFriendIds.push(friendKey);
				checkedFriendIdSet.add(friendKey);
			});
			const excludedFriendIds = Object.keys(this.excludedFriendIdMap).filter((friendId) => resolveIdKey(friendId));
			const excludedFriendIdSet = new Set(excludedFriendIds.map((friendId) => resolveIdKey(friendId)));
			const groupExcludedFriendIds = Object.keys(this.groupExcludedFriendIdMap).filter((friendId) => resolveIdKey(friendId));
			const groupExcludedFriendIdSet = new Set(groupExcludedFriendIds.map((friendId) => resolveIdKey(friendId)));
			const checkedFriendGroupIds = Object.keys(this.checkedFriendGroupIdMap)
				.filter((groupId) => this.friendGroupStore.findGroup(groupId));
			const checkedFriendGroupIdSet = new Set(checkedFriendGroupIds.map((groupId) => resolveIdKey(groupId)));
			const checkedFriendGroups = checkedFriendGroupIds
				.map((groupId) => this.friendGroupStore.findGroup(groupId))
				.filter(Boolean);
			const isCoveredByCheckedGroup = (friend) => {
				const groupKey = resolveIdKey(friend && friend.groupId);
				return Boolean(groupKey && checkedFriendGroupIdSet.has(groupKey));
			};
			const showFriends = [];
			const checkedFriendMap = new Map();
			const checkedFriendKeySet = new Set();
			const addCheckedFriend = (friend) => {
				if (!friend || friend.deleted) {
					return;
				}
				const friendKey = resolveIdKey(friend.id);
				if (!friendKey || checkedFriendKeySet.has(friendKey)) {
					return;
				}
				checkedFriendKeySet.add(friendKey);
				checkedFriendMap.set(friendKey, friend);
			};
			const keyword = this.normalizedSearchText;
			this.friends.forEach((friend) => {
				if (!friend) {
					return;
				}
				const friendKey = resolveIdKey(friend.id);
				if (!friendKey) {
					return;
				}
				if (this.allFriendsSelected) {
					if (!excludedFriendIdSet.has(friendKey)) {
						addCheckedFriend(friend);
					}
					if (matchesPreparedContactKeyword(friend[CONTACT_SEARCH_TEXT_KEY], keyword)) {
						showFriends.push(friend);
					}
					return;
				}
				if (isCoveredByCheckedGroup(friend)) {
					return;
				}
				if (matchesPreparedContactKeyword(friend[CONTACT_SEARCH_TEXT_KEY], keyword)) {
					showFriends.push(friend);
				}
			});
			if (!this.allFriendsSelected) {
				checkedFriendGroups.forEach((group) => {
					resolveFriendGroupMembers(group, this.friendStore, this.friendGroupStore).forEach((friend) => {
						if (groupExcludedFriendIdSet.has(resolveIdKey(friend && friend.id))) {
							return;
						}
						addCheckedFriend(friend);
					});
				});
				checkedFriendIdSet.forEach((friendKey) => {
					const friend = this.friendLookupMap[friendKey];
					if (friend) {
						addCheckedFriend(friend);
					}
				});
			}
			const checkedFriends = Array.from(checkedFriendMap.values());
			const selectedFriendIds = checkedFriends.map((friend) => friend.id);
			const selectedFriendIdSet = new Set(selectedFriendIds.map((friendId) => resolveIdKey(friendId)).filter(Boolean));
			const sourceFriendGroups = this.allFriendsSelected
				? []
				: filterFriendGroupOptions(
					this.friendGroupStore.groups.map((group) => buildFriendGroupOption(group, this.friendStore, this.friendGroupStore)),
					keyword
				).filter((group) => {
					const groupKey = resolveIdKey(group && group.id);
					if (!groupKey) {
						return false;
					}
					if (checkedFriendGroupIdSet.has(groupKey)) {
						return true;
					}
					return countFriendGroupNewMembers(group, selectedFriendIdSet) > 0;
				});
			const showFriendGroups = sourceFriendGroups;
			let visibleSelectableFriendIds = [];
			let visibleSelectableFriendGroupIds = [];
			if (this.currentSourceTab === 'friendGroup') {
				visibleSelectableFriendGroupIds = sourceFriendGroups
					.map((group) => group && group.id)
					.filter(Boolean);
			} else {
				visibleSelectableFriendIds = showFriends.map((friend) => friend.id);
			}
			return {
				checkedFriendIds,
				excludedFriendIds,
				checkedFriendGroupIds,
				checkedFriendGroups,
				checkedFriends,
				selectedFriendIds,
				showFriends,
				showFriendGroups,
				visibleSelectableFriendIds,
				visibleSelectableFriendGroupIds
			};
		},
		checkedFriendIds() {
			return this.friendSelectionState.checkedFriendIds;
		},
		showFriends() {
			return this.friendSelectionState.showFriends;
		},
		showFriendGroups() {
			return this.friendSelectionState.showFriendGroups;
		},
			sourceSelectableCount() {
				return this.currentListCount;
			},
			sourceCheckedCount() {
				if (this.currentSourceTab === 'friendGroup') {
					return this.checkedFriendGroupIds.length;
				}
				if (this.allFriendsSelected) {
					if (this.normalizedSearchText) {
						return this.showFriends.filter((friend) => !this.excludedFriendIdMap[String(friend && friend.id)]).length;
					}
					return this.checkCount;
				}
				if (this.normalizedSearchText) {
					const visibleIds = new Set(this.friendSelectionState.visibleSelectableFriendIds.map((friendId) => String(friendId)));
					return this.checkedFriendIds.filter((friendId) => visibleIds.has(String(friendId))).length;
				}
				return this.checkedFriendIds.length;
			},
			isAllVisibleChecked() {
				return this.sourceSelectableCount > 0 && this.sourceCheckedCount === this.sourceSelectableCount;
			},
			isSelectAllIndeterminate() {
				return this.sourceCheckedCount > 0 && this.sourceCheckedCount < this.sourceSelectableCount;
			},
		checkedFriends() {
			return this.friendSelectionState.checkedFriends;
		},
		excludedFriendIds() {
			return this.friendSelectionState.excludedFriendIds
				.map((friendId) => Number(friendId))
				.filter((friendId) => Number.isFinite(friendId) && friendId > 0);
		},
		groupExcludedFriendIds() {
			return Object.keys(this.groupExcludedFriendIdMap)
				.map((friendId) => Number(friendId))
				.filter((friendId) => Number.isFinite(friendId) && friendId > 0);
		},
		checkedFriendGroupIds() {
			return this.friendSelectionState.checkedFriendGroupIds;
		},
		checkedFriendGroups() {
			return this.friendSelectionState.checkedFriendGroups;
		},
		checkedSelectionItems() {
			return this.checkedFriends;
		},
				checkedSelectionItemsView() {
					const limit = Math.max(CHECKED_RESULT_PAGE_SIZE, Math.floor(Number(this.checkedViewLimit) || 0));
					if (this.checkedSelectionItems.length <= limit) {
						return this.checkedSelectionItems;
					}
					return this.checkedSelectionItems.slice(0, limit);
				},
			selectedFriendIds() {
			return this.friendSelectionState.selectedFriendIds;
		},
		checkCount() {
			if (this.allFriendsSelected) {
				return Math.max(this.friendSourceTotalCount - this.excludedFriendIds.length, 0);
			}
			if (this.checkedFriendGroupIds.length > 0) {
				const approx = Math.max(this.checkedFriendGroupApproxMemberCount - this.groupExcludedFriendIds.length, 0);
				return Math.max(approx, this.selectedFriendIds.length);
			}
			return this.checkedFriendIds.length;
		},
		friendSourceTotalCount() {
			const total = Math.max(0, Math.floor(Number(this.friendStore.page.total || 0)));
			return total || this.friends.length;
		},
		checkedFriendGroupApproxMemberCount() {
			return this.checkedFriendGroups.reduce((sum, group) => {
				return sum + Math.max(0, Math.floor(Number(group && group.memberCount) || 0));
			}, 0);
		},
		searchPlaceholder() {
			return this.currentSourceTab === 'friendGroup' ? '搜索好友分组名称' : '搜索好友昵称或完整账号';
		},
				currentListCount() {
					if (this.currentSourceTab === 'friendGroup') {
						if (this.normalizedSearchText) {
							return this.showFriendGroups.length;
						}
						return this.friendGroupSourceSelectableTotalCount;
					}
				if (this.normalizedSearchText) {
					return this.showFriends.length;
				}
				return this.friendSourceTotalCount;
			},
		allVisibleCheckedProxy: {
			get() {
				return this.isAllVisibleChecked;
			},
			set(checked) {
				this.onToggleGlobalSelectAll(checked);
			}
		},
		currentEmptyTitle() {
			if (this.normalizedSearchText) {
				return '无结果';
			}
			return this.currentSourceTab === 'friendGroup' ? '暂无可选好友分组' : '暂无可选好友';
		},
		friendGroupSourceSelectableTotalCount() {
			return Math.max(
				this.friendGroupStore.groups.length,
				Math.floor(Number(this.friendGroupStore.page.total || 0))
			);
		},
			isSelectAllDisabled() {
				if (this.currentSourceTab === 'friendGroup') {
					return this.showFriendGroups.length === 0;
				}
				return this.friendSourceTotalCount === 0;
			},
		checkedEmptyIcon() {
			return this.selectAllLoading ? 'el-icon-loading' : 'el-icon-user';
		},
		checkedEmptyTitle() {
			return this.selectAllLoading ? '正在加载数据' : '至少选择 1 位好友';
		}
	},
	methods: {
		getSourceFriendKey(friend) {
			const key = resolveIdKey(friend && friend.id);
			return key ? `source:${key}` : '';
		},
		getCheckedSelectionRowKey(item) {
			if (!item) {
				return '';
			}
			if (item.type === 'GROUP') {
				return item.targetId ? `checked:group:${item.targetId}` : '';
			}
			return item.targetId ? `checked:friend:${item.targetId}` : '';
		},
		resetState() {
			this.show = false;
			this.groupName = "";
			this.searchText = "";
			this.sourceTabIdx = 0;
			this.friends = [];
			this.friendLookupMap = Object.create(null);
				this.friendsHydrating = false;
				this.friendHydrationScheduled = false;
				this.checkedFriendIdMap = {};
				this.excludedFriendIdMap = {};
				this.groupExcludedFriendIdMap = {};
					this.allFriendsSelected = false;
					this.checkedFriendGroupIdMap = {};
					this.checkedViewLimit = CHECKED_RESULT_PAGE_SIZE;
					this.selectAllLoading = false;
				this.submitting = false;
			},
		open() {
			this.show = true;
			this.groupName = "";
			this.searchText = "";
			this.sourceTabIdx = 0;
			this.submitting = false;
			this.checkedFriendIdMap = {};
			this.excludedFriendIdMap = {};
				this.groupExcludedFriendIdMap = {};
				this.allFriendsSelected = false;
				this.checkedFriendGroupIdMap = {};
				this.checkedViewLimit = CHECKED_RESULT_PAGE_SIZE;
				this.selectAllLoading = false;
			this.preloadChatRoute();
			this.scheduleFriendHydration();
			this.scheduleFriendGroupRefresh();
		},
		scheduleFriendHydration() {
			this.friends = [];
			this.friendLookupMap = Object.create(null);
			this.friendsHydrating = true;
			this.friendHydrationScheduled = false;
			this.$nextTick(() => {
				const hydrate = () => {
					if (!this.show) {
						return;
					}
					this.hydrateFriendsFromStore();
				};
				if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
					window.requestAnimationFrame(hydrate);
					return;
				}
				setTimeout(hydrate, 0);
			});
		},
		scheduleFriendRefreshFromStore() {
			if (!this.show || this.friendHydrationScheduled) {
				return;
			}
			this.friendHydrationScheduled = true;
			const hydrate = () => {
				this.friendHydrationScheduled = false;
				if (!this.show) {
					return;
				}
				this.hydrateFriendsFromStore();
			};
			if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
				window.requestAnimationFrame(hydrate);
				return;
			}
			setTimeout(hydrate, 0);
		},
		hydrateFriendsFromStore() {
			const friends = this.friendStore.friends
				.filter((friend) => friend && !friend.deleted)
				.map((friend) => attachContactSearchText(friend));
			const nextFriendLookupMap = Object.create(null);
			friends.forEach((friend) => {
				if (friend && friend.id) {
					nextFriendLookupMap[String(friend.id)] = friend;
				}
			});
			this.friendLookupMap = nextFriendLookupMap;
			this.friends = friends;
			this.friendsHydrating = false;
		},
			onFriendListReachBottom() {
				if (this.currentSourceTab !== 'friend' || this.normalizedSearchText) {
					return;
				}
			if (this.friendStore.page.loading
				|| !this.friendStore.page.more
				|| !this.friendStore.page.cursor) {
				return;
			}
			this.friendStore.loadMoreFriendsFromCursor({
				maxPages: 1
			}).then(() => {
				this.hydrateFriendsFromStore();
				}).catch((error) => {
					console.warn('QuickCreateGroup 滚动续拉好友分页失败', error);
				});
			},
			async onCheckedListReachBottom() {
				const nextLimit = Math.max(CHECKED_RESULT_PAGE_SIZE, Math.floor(Number(this.checkedViewLimit) || 0)) + CHECKED_RESULT_PAGE_SIZE;
				const maxCount = Math.max(this.checkCount, this.checkedSelectionItems.length);
				this.checkedViewLimit = Math.min(maxCount, nextLimit);
				await this.ensureCheckedWindowHydrated();
			},
			async ensureCheckedWindowHydrated() {
				if (this.allFriendsSelected) {
					await this.ensureCheckedWindowHydratedForAllSelected();
					return;
				}
				await this.ensureCheckedWindowHydratedForCheckedGroups();
			},
		async ensureCheckedWindowHydratedForAllSelected() {
			if (!this.show || !this.allFriendsSelected) {
				return;
			}
				const targetSize = Math.max(CHECKED_RESULT_PAGE_SIZE, Math.floor(Number(this.checkedViewLimit) || 0));
				while (this.show && this.allFriendsSelected && this.checkedSelectionItems.length < targetSize) {
					if (this.friendsHydrating
						|| this.friendStore.page.loading
						|| !this.friendStore.page.more
						|| !this.friendStore.page.cursor) {
						break;
					}
					const beforeLength = this.checkedSelectionItems.length;
					const beforeCursor = this.friendStore.page.cursor;
					try {
						await this.friendStore.loadMoreFriendsFromCursor({
							maxPages: 1
						});
						this.hydrateFriendsFromStore();
						await this.$nextTick();
					} catch (error) {
						console.warn('QuickCreateGroup 全选结果窗口补齐失败', error);
						break;
					}
					if (this.checkedSelectionItems.length <= beforeLength && this.friendStore.page.cursor === beforeCursor) {
						break;
					}
				}
			},
			async ensureCheckedWindowHydratedForCheckedGroups() {
				if (!this.show || this.allFriendsSelected || this.checkedFriendGroupIds.length === 0) {
					return;
				}
				const targetSize = Math.max(CHECKED_RESULT_PAGE_SIZE, Math.floor(Number(this.checkedViewLimit) || 0));
				const activeGroupIds = this.checkedFriendGroupIds.slice();
				for (let idx = 0; idx < activeGroupIds.length; idx += 1) {
					if (!this.show || this.allFriendsSelected || this.checkedSelectionItems.length >= targetSize) {
						return;
					}
					const groupId = activeGroupIds[idx];
					if (!this.checkedFriendGroupIdMap[String(groupId)]) {
						continue;
					}
					try {
						await this.friendGroupStore.ensureGroupFriendsFullyLoaded(groupId, {
							limit: 30,
							includeOnline: false
						});
					} catch (error) {
						console.warn('QuickCreateGroup 加载好友分组成员失败', error);
						continue;
					}
					this.hydrateFriendsFromStore();
					await this.$nextTick();
				}
			},
		preloadChatRoute() {
			preloadChatRouteView().catch((error) => {
				console.warn('预加载会话页面失败:', error);
			});
		},
		scheduleFriendGroupRefresh() {
			if (this.friendGroupStore.groups.length > 0) {
				return;
			}
			const refresh = () => {
				if (!this.show || this.friendGroupStore.groups.length > 0) {
					return;
				}
				this.refreshFriendGroups().catch((error) => {
					this.$message.error(this.extractErrorMessage(error) || '刷新好友分组失败');
				});
			};
			if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
				window.requestIdleCallback(refresh, {
					timeout: 1000
				});
				return;
			}
			setTimeout(refresh, 200);
		},
			async refreshFriendGroups() {
				const groups = await this.friendGroupStore.loadGroups({
					limit: 30
				});
				this.hydrateFriendsFromStore();
				return groups;
			},
			close() {
				this.resetState();
				this.$emit('close');
			},
			onSourceTabChange(idx) {
				this.sourceTabIdx = idx;
				if (this.currentSourceTab === 'friendGroup'
					&& this.friendGroupStore.groups.length === 0
					&& !this.friendGroupStore.page.loading) {
					this.refreshFriendGroups().catch((error) => {
						console.warn('QuickCreateGroup 切换好友分组后刷新失败', error);
					});
				}
			},
			onRemoveFriend(friend) {
				this.setFriendChecked(friend, false);
			},
			onSwitchCheck(friend) {
				this.setFriendChecked(friend, !this.isFriendChecked(friend));
			},
			isFriendCoveredByCheckedGroup(friend) {
				if (!friend || !friend.id || this.checkedFriendGroupIds.length === 0) {
					return false;
				}
				const friendKey = resolveIdKey(friend.id);
				if (!friendKey) {
					return false;
				}
				const directGroupKey = resolveIdKey(friend.groupId);
				if (directGroupKey && this.checkedFriendGroupIdMap[directGroupKey]) {
					return true;
				}
				return this.checkedFriendGroupIds.some((groupId) => {
					return this.friendGroupStore.findGroupFriends(groupId).some((member) => {
						return resolveIdKey(member && member.id) === friendKey;
					});
				});
			},
			isFriendChecked(friendOrId) {
				const friend = friendOrId && typeof friendOrId === 'object'
					? friendOrId
					: this.findLocalFriend(friendOrId);
				const friendKey = resolveIdKey(friend && friend.id ? friend.id : friendOrId);
				if (!friendKey) {
					return false;
				}
				if (this.allFriendsSelected) {
					return !this.excludedFriendIdMap[friendKey];
				}
				if (this.groupExcludedFriendIdMap[friendKey]) {
					return false;
				}
				if (this.isFriendCoveredByCheckedGroup(friend || { id: friendOrId })) {
					return true;
				}
				return Boolean(this.checkedFriendIdMap[friendKey]);
			},
		findLocalFriend(friendId) {
			return this.friendLookupMap[String(friendId)] || null;
		},
			setFriendChecked(friend, checked) {
				if (!friend || !friend.id) {
					return;
				}
				const friendKey = String(friend.id);
				if (this.allFriendsSelected) {
					if (checked) {
						if (this.excludedFriendIdMap[friendKey]) {
							this.$delete(this.excludedFriendIdMap, friendKey);
						}
						return;
					}
					if (!this.excludedFriendIdMap[friendKey]) {
						this.$set(this.excludedFriendIdMap, friendKey, Number(friend.id));
					}
					return;
				}
				const coveredByCheckedGroup = this.isFriendCoveredByCheckedGroup(friend);
				if (coveredByCheckedGroup) {
					if (checked) {
						if (this.groupExcludedFriendIdMap[friendKey]) {
							this.$delete(this.groupExcludedFriendIdMap, friendKey);
						}
						return;
					}
					if (!this.groupExcludedFriendIdMap[friendKey]) {
						this.$set(this.groupExcludedFriendIdMap, friendKey, Number(friend.id));
					}
					return;
				}
					if (checked) {
						if (!this.checkedFriendIdMap[friendKey]) {
							this.$set(this.checkedFriendIdMap, friendKey, true);
							if (this.checkedFriendIds.length <= this.checkedViewLimit + 1) {
							this.checkedViewLimit = this.checkedFriendIds.length;
						}
					}
					return;
				}
			if (this.checkedFriendIdMap[friendKey]) {
				this.$delete(this.checkedFriendIdMap, friendKey);
			}
		},
			onClearChecked() {
				this.checkedFriendIdMap = {};
				this.excludedFriendIdMap = {};
				this.groupExcludedFriendIdMap = {};
				this.allFriendsSelected = false;
				this.checkedFriendGroupIdMap = {};
				this.checkedViewLimit = CHECKED_RESULT_PAGE_SIZE;
			},
			setSourceChecked(checked) {
				if (this.currentSourceTab === 'friendGroup') {
					const nextGroupMap = { ...this.checkedFriendGroupIdMap };
					const groupIds = this.normalizedSearchText
						? this.getVisibleSelectableFriendGroupIds()
						: this.friendGroupStore.groups.map((group) => group && group.id).filter(Boolean);
					groupIds.forEach((groupId) => {
						if (checked) {
							nextGroupMap[String(groupId)] = true;
						} else {
							delete nextGroupMap[String(groupId)];
						}
					});
					this.checkedFriendGroupIdMap = nextGroupMap;
					return;
				}
				if (checked) {
					this.allFriendsSelected = true;
					this.checkedFriendIdMap = {};
					this.excludedFriendIdMap = {};
					this.groupExcludedFriendIdMap = {};
					this.checkedFriendGroupIdMap = {};
					return;
				}
				if (!checked && this.allFriendsSelected) {
					this.allFriendsSelected = false;
					this.excludedFriendIdMap = {};
					return;
				}
				this.groupExcludedFriendIdMap = {};
					const nextCheckedMap = { ...this.checkedFriendIdMap };
				const friendIds = this.normalizedSearchText
					? this.getVisibleSelectableFriendIds()
					: this.friends
						.map((friend) => friend && friend.id)
						.filter(Boolean);
				friendIds.forEach((friendId) => {
					if (checked) {
						nextCheckedMap[String(friendId)] = true;
					} else {
						delete nextCheckedMap[String(friendId)];
					}
			});
				this.checkedFriendIdMap = nextCheckedMap;
			},
			async ensureCurrentSourceFullyLoaded() {
				if (this.currentSourceTab === 'friend') {
					await this.friendStore.ensureFriendListFullyLoaded({
						limit: 30
					});
					return;
				}
				await this.friendGroupStore.ensureGroupListFullyLoaded({
					limit: 30
				});
			},
			async onToggleGlobalSelectAll(checked) {
				if (this.selectAllLoading) {
					return;
				}
			const shouldLoadSource = checked;
			if (shouldLoadSource) {
				this.selectAllLoading = true;
				try {
					await this.ensureCurrentSourceFullyLoaded();
					this.hydrateFriendsFromStore();
				} catch (error) {
					console.warn('QuickCreateGroup 全量加载源数据失败', error);
					this.$message.error(this.extractErrorMessage(error) || '加载数据失败，请稍后重试');
					return;
				} finally {
					this.selectAllLoading = false;
				}
				}
					this.setSourceChecked(checked);
					if (this.allFriendsSelected) {
						this.checkedViewLimit = CHECKED_RESULT_PAGE_SIZE;
						await this.ensureCheckedWindowHydratedForAllSelected();
					} else if (checked && this.currentSourceTab === 'friendGroup') {
						await this.ensureCheckedWindowHydratedForCheckedGroups();
					}
				},
			getVisibleSelectableFriendIds() {
				return this.friendSelectionState.visibleSelectableFriendIds;
			},
			getVisibleSelectableFriendGroupIds() {
				return this.friendSelectionState.visibleSelectableFriendGroupIds;
			},
			onFriendGroupReachBottom() {
				if (this.currentSourceTab !== 'friendGroup' || this.normalizedSearchText) {
					return;
				}
				if (this.friendGroupStore.page.loading
					|| !this.friendGroupStore.page.more
					|| !this.friendGroupStore.page.cursor) {
					return;
				}
				this.friendGroupStore.loadMoreGroupsFromCursor({
					limit: 30,
					maxPages: 1
				}).catch((error) => {
					console.warn('QuickCreateGroup 滚动续拉好友分组失败', error);
				});
			},
			async onToggleFriendGroup(payload) {
				const group = payload && payload.group ? payload.group : null;
				const checked = payload && typeof payload.checked === 'boolean' ? payload.checked : false;
				if (!group || !group.id || Number(group.memberCount || 0) <= 0) {
					return;
				}
				const groupKey = String(group.id);
				if (checked) {
					if (this.checkedFriendGroupIdMap[groupKey]) {
						this.$message.info('该好友分组成员已全部加入当前选择');
						return;
					}
					this.allFriendsSelected = false;
					this.excludedFriendIdMap = {};
					this.groupExcludedFriendIdMap = {};
					this.$set(this.checkedFriendGroupIdMap, groupKey, true);
					try {
						await this.friendGroupStore.ensureGroupFriendsFullyLoaded(group.id, {
							limit: 30,
							includeOnline: false
						});
					} catch (error) {
						console.warn('QuickCreateGroup 预加载好友分组成员失败', error);
					}
					await this.ensureCheckedWindowHydratedForCheckedGroups();
					return;
				}
				if (this.checkedFriendGroupIdMap[groupKey]) {
					this.$delete(this.checkedFriendGroupIdMap, groupKey);
				}
		},
		onRemoveCheckedItem(item) {
			if (!item) {
				return;
			}
			this.onRemoveFriend(item);
		},
		buildDefaultGroupName(selectedFriends = this.checkedFriends) {
			if (this.allFriendsSelected) {
				return `${this.mine.nickName || '新建'}的群聊`;
			}
			if (this.checkedFriendGroupIds.length > 0) {
				const groupNames = this.checkedFriendGroups.map((group) => group && group.name).filter(Boolean);
				const names = [this.mine.nickName].concat(groupNames).filter(Boolean);
				let groupResult = "";
				names.forEach((name) => {
					const nextText = groupResult ? `${groupResult}、${name}` : name;
					if (nextText.length <= 20) {
						groupResult = nextText;
					}
				});
				return groupResult || `${this.mine.nickName || '新建'}的群聊`;
			}
			const names = [this.mine.nickName]
				.concat((selectedFriends || []).map((friend) => friend.showNickName || friend.nickName))
				.filter(Boolean);
			if (names.length === 0) {
				return "新群聊";
			}
			let result = "";
			names.forEach((name) => {
				const nextText = result ? `${result}、${name}` : name;
				if (nextText.length <= 20) {
					result = nextText;
				}
			});
			if (!result) {
				result = `${this.mine.nickName || '新建'}的群聊`;
			}
			return result.slice(0, 20);
		},
		normalizeGroupName(name) {
			const safeName = String(name || '')
				.replace(/[\u202A-\u202E\u2066-\u2069]/g, '')
				.trim();
			if (!safeName) {
				return "";
			}
			return safeName.slice(0, 20);
		},
		buildCreatePayload(groupName) {
			const payload = {
				name: groupName
			};
			if (this.allFriendsSelected) {
				payload.selectionMode = 'ALL';
				if (this.excludedFriendIds.length > 0) {
					payload.excludedFriendIds = this.excludedFriendIds.slice();
				}
				return payload;
			}
			if (this.checkedFriendGroupIds.length > 0) {
				payload.selectionMode = 'GROUPS';
				payload.friendGroupIds = this.checkedFriendGroupIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
				if (this.selectedFriendIds.length > 0) {
					payload.friendIds = this.selectedFriendIds;
				}
				return payload;
			}
			payload.selectionMode = 'IDS';
			payload.friendIds = this.selectedFriendIds;
			return payload;
		},
		extractErrorMessage(error) {
			if (error && typeof error.message === "string" && error.message) {
				return error.message;
			}
			if (error && typeof error.msg === "string" && error.msg) {
				return error.msg;
			}
			if (error && error.response && error.response.data) {
				const responseData = error.response.data;
				if (typeof responseData === "string" && responseData) {
					return responseData;
				}
				if (typeof responseData.message === "string" && responseData.message) {
					return responseData.message;
				}
			}
			return "";
		},
		async openGroupChat(group) {
			const chat = {
				type: 'GROUP',
				targetId: group.id,
				showName: resolveGroupChatDisplayName(group) || group.name,
				headImage: group.headImageThumb
			};
			this.chatStore.openChat(chat);
			const openedChat = this.chatStore.findChatByGroup(group.id);
			if (openedChat) {
				Object.defineProperty(openedChat, '_deferInitialGroupSync', {
					value: true,
					writable: true,
					configurable: true
				});
			}
			this.chatStore.setActiveChat(openedChat || chat);
			await this.$router.push("/home/chat").catch((error) => {
				if (error && error.name !== "NavigationDuplicated") {
					throw error;
				}
			});
		},
		async onOk() {
			if (this.submitting) {
				return;
			}
			const selectedFriendIds = this.selectedFriendIds.slice();
			const selectedFriends = this.checkedFriends.slice();
			if (!this.allFriendsSelected && selectedFriendIds.length === 0 && this.checkedFriendGroupIds.length === 0) {
				this.$message.warning('请至少选择 1 位好友');
				return;
			}
			if (this.checkCount + 1 > this.maxMemberCount) {
				this.$message.warning(`群聊人数不能超过 ${this.maxMemberCount} 人`);
				return;
			}
			const groupName = this.normalizeGroupName(this.groupName)
				|| this.normalizeGroupName(this.buildDefaultGroupName(selectedFriends));
			if (!groupName) {
				this.$message.warning('群聊名称生成失败，请稍后重试');
				return;
			}
			this.submitting = true;
			try {
				const group = await this.$http({
					url: "/group/create",
					method: 'post',
					data: this.buildCreatePayload(groupName),
					_skipErrorMessage: true
				});
				this.groupStore.addGroup(group);
				this.close();
				await this.$nextTick();
				try {
					await this.openGroupChat(group);
				} catch (error) {
					console.warn('打开新建群聊失败:', error);
				}
			} catch (error) {
				const failText = this.extractErrorMessage(error);
				this.$message.error(failText ? `创建群聊失败：${failText}` : '创建群聊失败');
			} finally {
				this.submitting = false;
			}
		}
	},
	watch: {
		dialogVisible(newVal) {
			if (newVal) {
				this.open();
				return;
			}
			if (!newVal) {
				this.resetState();
			}
		},
		'friendStore.friends.length'(nextLen, prevLen) {
			if (!this.show || nextLen === prevLen) {
				return;
			}
			this.scheduleFriendRefreshFromStore();
		},
		'friendStore.page.total'(nextTotal, prevTotal) {
			if (!this.show) {
				return;
			}
			const nextVal = Math.max(0, Math.floor(Number(nextTotal || 0)));
			const prevVal = Math.max(0, Math.floor(Number(prevTotal || 0)));
			if (nextVal === prevVal) {
				return;
			}
			// total 有值但本地快照尚未同步到组件时，主动补一次轻量水合，避免短暂空列表观感
			if (nextVal > 0 && this.friends.length === 0 && !this.friendsHydrating) {
				this.scheduleFriendRefreshFromStore();
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.quick-create-group {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(220px, 236px);
	gap: 10px;
	height: min(428px, calc(100vh - 184px));
	min-height: 0;
	overflow: hidden;

	::v-deep .el-scrollbar__wrap {
		overflow-x: hidden;
	}

	.left-box,
	.right-box {
		display: flex;
		flex-direction: column;
		min-width: 0;
		border: 1px solid var(--im-surface-panel-border);
		border-radius: 12px;
		background: var(--im-surface-panel-soft);
		overflow: hidden;
	}

	.panel-head {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 10px 12px;
		border-bottom: 1px solid var(--im-surface-panel-border);
	}

	.panel-title {
		font-size: 14px;
		font-weight: 600;
		color: var(--im-text-color);
		text-align: left;
	}

	.group-name-input {
		::v-deep .el-input__inner {
			font-weight: 600;
		}
	}

	.result-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 24px;
		height: 24px;
		padding: 0 7px;
		border-radius: 4px;
		background: rgba(var(--im-primary-rgb), 0.08);
		color: var(--im-color-primary);
		font-size: 12px;
		font-weight: 600;
	}

	.selection-head {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
	}

	.compact-title {
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}

	.inline-count {
		color: var(--im-color-primary);
	}

	.select-tools {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}

	.select-tools__actions {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		margin-left: auto;
	}

	.select-loading {
		color: var(--im-color-primary);
		font-size: 14px;
	}

	.scroll-box {
		flex: 1;
		min-height: 0;
	}

	.left-box {
		::v-deep .virtual-content {
			padding: 4px 0;
		}
	}

	.list-empty-state {
		height: 100%;
		min-height: 180px;
	}

	.checked-item {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 10px 12px;
		cursor: pointer;
		transition: background-color 0.2s ease;

		&:hover {
			background: rgba(var(--im-primary-rgb), 0.05);
		}
	}

	.checked-main {
		flex: 1;
		min-width: 0;
		text-align: left;
	}

	.checked-icon {
		width: 34px;
		height: 34px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 34px;
		border-radius: 50%;
		background: rgba(var(--im-primary-rgb), 0.1);
		color: var(--im-color-primary);
		font-size: 17px;
	}

	.checked-name {
		font-size: 13px;
		font-weight: 600;
		color: var(--im-text-color);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.close {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: var(--im-text-color-light);
	}

	.icon-btn {
		padding: 0;
	}

	.checkbox {
		margin-left: auto;
	}

	&.is-mobile {
		grid-template-columns: minmax(0, 1fr);
		height: calc(100vh - 178px);
	}
}
</style>
